/**
 * SetupMatter Service
 *
 * Axios-based API client for SetupMatter entity with authentication,
 * error handling, and optional caching support.
 *
 * Generated by CodeGen OS v1.0.0
 * Generated at: 2026-04-25T14:23:21.016164Z
 */

// Vite environment type augmentation
declare global {
  interface ImportMeta {
    env: {
      VITE_API_BASE_URL?: string;
      [key: string]: string | undefined;
    };
  }
}

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import type {
  SetupMatter,
  SetupMatterCreateRequest,
  SetupMatterUpdateRequest,
  SetupMatterListResponse,
  SetupMatterFilterOptions,
} from '../types/setup_matter.types';

// ---- CODAIC:CUSTOM IMPORTS ----
// Add custom imports below. This section is preserved across regeneration.
// ---- CODAIC:END CUSTOM ----

// API Base URL - configurable via environment variable
// Empty string default means requests use relative paths (going through Vite proxy in dev)
const API_BASE_URL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE_URL) ||
  '';

// Tenant ID - required by multi-tenant backend
const TENANT_ID =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_TENANT_ID) ||
  (typeof localStorage !== 'undefined' && localStorage.getItem('tenant_id')) ||
  '00000000-0000-0000-0000-000000000000';

// Default cache TTL in milliseconds (5 minutes)
const DEFAULT_CACHE_TTL_MS = 5 * 60 * 1000;

// Resource path — single source of truth for this entity's API prefix.
// Use this in CODAIC:CUSTOM methods: `${RESOURCE_PATH}/my-action`
const RESOURCE_PATH = '/api/v1/setup-matters';

/**
 * SetupMatter service error class for typed error handling
 */
export class SetupMatterServiceError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public errorCode?: string
  ) {
    super(message);
    this.name = 'SetupMatterServiceError';
  }
}

/**
 * Cache entry structure for in-memory caching
 */
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

/**
 * SetupMatter Service Class
 *
 * Provides CRUD operations for SetupMatter entities with:
 * - Bearer token authentication
 * - Typed error handling
 * - Optional in-memory caching
 * - Filter support
 */
class SetupMatterService {
  private client: AxiosInstance;
  private cache: Map<string, CacheEntry<SetupMatter[]>>;
  private cacheTTL: number;

  constructor(baseURL?: string, cacheTTL: number = DEFAULT_CACHE_TTL_MS) {
    this.client = axios.create({
      baseURL: baseURL || API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.cache = new Map();
    this.cacheTTL = cacheTTL;

    // Add request interceptor for authentication and tenant context
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token =
          typeof localStorage !== 'undefined'
            ? localStorage.getItem('access_token')
            : null;

        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Always send tenant ID header (required by multi-tenant backend)
        if (config.headers) {
          const tenantId =
            (typeof localStorage !== 'undefined' && localStorage.getItem('tenant_id')) ||
            TENANT_ID;
          config.headers['X-Tenant-ID'] = tenantId;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          console.warn('[SetupMatterService] Unauthorized — clearing session');
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('token_expiry');
          if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Check if cache entry is still valid
   */
  private isCacheValid(entry: CacheEntry<SetupMatter[]> | undefined): boolean {
    if (!entry) return false;
    const now = Date.now();
    return now - entry.timestamp < entry.ttl;
  }

  /**
   * Get cached data if valid
   */
  private getCached(key: string): SetupMatter[] | null {
    const entry = this.cache.get(key);
    if (this.isCacheValid(entry)) {
      console.debug(`[SetupMatterService] Cache hit for key: ${key}`);
      return entry!.data;
    }
    return null;
  }

  /**
   * Set cache entry
   */
  private setCache(key: string, data: SetupMatter[]): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: this.cacheTTL,
    });
    console.debug(
      `[SetupMatterService] Cached ${data.length} items for key: ${key}`
    );
  }

  /**
   * Handle API errors and transform to typed error
   */
  private handleError(error: unknown): SetupMatterServiceError {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<{
        detail?: string;
        message?: string;
        error_code?: string;
      }>;
      const message =
        axiosError.response?.data?.detail ||
        axiosError.response?.data?.message ||
        axiosError.message ||
        'An error occurred';
      const statusCode = axiosError.response?.status;
      const errorCode = axiosError.response?.data?.error_code;
      return new SetupMatterServiceError(message, statusCode, errorCode);
    }
    return new SetupMatterServiceError(
      error instanceof Error ? error.message : 'Unknown error'
    );
  }

  /**
   * Fetch all setup_matters with optional filters
   *
   * @param filters - Optional filter options
   * @param forceRefresh - Skip cache and fetch fresh data
   * @returns Array of SetupMatter objects
   * @throws SetupMatterServiceError on fetch failure
   */
  async getAll(
    filters?: SetupMatterFilterOptions,
    forceRefresh: boolean = false
  ): Promise<SetupMatter[]> {
    const cacheKey = `all_setup_matters_${JSON.stringify(filters || {})}`;

    // Check cache first
    if (!forceRefresh) {
      const cached = this.getCached(cacheKey);
      if (cached) {
        return cached;
      }
    }

    const url = `${RESOURCE_PATH}/`;
    try {
      console.log(`[SetupMatterService] GET ${url}`, { params: filters });
      const response = await this.client.get<
        SetupMatter[] | SetupMatterListResponse
      >(url, {
        params: { page_size: 200, ...(filters as Record<string, unknown> | undefined) },
      });

      // Handle both array response and wrapped response
      const data = response.data;
      const items = Array.isArray(data)
        ? data
        : (data as SetupMatterListResponse).items ||
          (data as SetupMatterListResponse).setup_matters ||
          [];

      console.log(`[SetupMatterService] GET ${url} → ${response.status} OK`, {
        count: items.length,
        firstItem: items[0] ? { id: items[0].id } : null,
      });

      this.setCache(cacheKey, items);
      return items;
    } catch (error) {
      const serviceError = this.handleError(error);
      console.error(`[SetupMatterService] GET ${url} → FAILED`, {
        status: serviceError.statusCode,
        message: serviceError.message,
        errorCode: serviceError.errorCode,
      });
      throw serviceError;
    }
  }

  /**
   * Get a single setup_matter by ID
   *
   * @param id - SetupMatter ID (UUID)
   * @returns SetupMatter object
   * @throws SetupMatterServiceError on fetch failure
   */
  async getById(id: string): Promise<SetupMatter> {
    const url = `${RESOURCE_PATH}/${id}`;
    try {
      console.log(`[SetupMatterService] GET ${url}`);
      const response = await this.client.get<SetupMatter>(url);
      console.log(`[SetupMatterService] GET ${url} → ${response.status} OK`, {
        id: response.data.id,
      });
      return response.data;
    } catch (error) {
      const serviceError = this.handleError(error);
      console.error(`[SetupMatterService] GET ${url} → FAILED`, {
        status: serviceError.statusCode,
        message: serviceError.message,
        errorCode: serviceError.errorCode,
      });
      throw serviceError;
    }
  }

  /**
   * Create a new setup_matter
   *
   * @param data - SetupMatter creation data
   * @returns Created SetupMatter object
   * @throws SetupMatterServiceError on creation failure
   */
  async create(data: SetupMatterCreateRequest): Promise<SetupMatter> {
    const url = `${RESOURCE_PATH}/`;
    try {
      console.log(`[SetupMatterService] POST ${url}`, { data });
      const response = await this.client.post<SetupMatter>(url, data);
      console.log(`[SetupMatterService] POST ${url} → ${response.status} Created`, {
        id: response.data.id,
      });
      // Invalidate cache on successful mutation
      this.clearCache();
      return response.data;
    } catch (error) {
      const serviceError = this.handleError(error);
      console.error(`[SetupMatterService] POST ${url} → FAILED`, {
        status: serviceError.statusCode,
        message: serviceError.message,
        errorCode: serviceError.errorCode,
      });
      throw serviceError;
    }
  }

  /**
   * Update an existing setup_matter
   *
   * @param id - SetupMatter ID (UUID)
   * @param data - Partial SetupMatter update data
   * @returns Updated SetupMatter object
   * @throws SetupMatterServiceError on update failure
   */
  async update(
    id: string,
    data: SetupMatterUpdateRequest
  ): Promise<SetupMatter> {
    const url = `${RESOURCE_PATH}/${id}`;
    try {
      console.log(`[SetupMatterService] PUT ${url}`, { data });
      const response = await this.client.put<SetupMatter>(url, data);
      console.log(`[SetupMatterService] PUT ${url} → ${response.status} OK`, {
        id: response.data.id,
      });
      // Invalidate cache on successful mutation
      this.clearCache();
      return response.data;
    } catch (error) {
      const serviceError = this.handleError(error);
      console.error(`[SetupMatterService] PUT ${url} → FAILED`, {
        status: serviceError.statusCode,
        message: serviceError.message,
        errorCode: serviceError.errorCode,
      });
      throw serviceError;
    }
  }

  /**
   * Delete a setup_matter
   *
   * @param id - SetupMatter ID (UUID)
   * @throws SetupMatterServiceError on deletion failure
   */
  async delete(id: string): Promise<void> {
    const url = `${RESOURCE_PATH}/${id}`;
    try {
      console.log(`[SetupMatterService] DELETE ${url}`);
      const response = await this.client.delete(url);
      console.log(`[SetupMatterService] DELETE ${url} → ${response.status} OK`, {
        deletedId: id,
      });
      // Invalidate cache on successful mutation
      this.clearCache();
    } catch (error) {
      const serviceError = this.handleError(error);
      console.error(`[SetupMatterService] DELETE ${url} → FAILED`, {
        status: serviceError.statusCode,
        message: serviceError.message,
        errorCode: serviceError.errorCode,
      });
      throw serviceError;
    }
  }

  /**
   * Get active setup_matters only
   *
   * @param forceRefresh - Skip cache and fetch fresh data
   * @returns Array of active SetupMatter objects
   */
  async getActive(forceRefresh: boolean = false): Promise<SetupMatter[]> {
    const all = await this.getAll(undefined, forceRefresh);
    return all.filter((item) => (item as any).is_active !== false);
  }

  /**
   * Search setup_matters by query string
   *
   * @param query - Search query string
   * @param forceRefresh - Skip cache and fetch fresh data
   * @returns Array of matching SetupMatter objects
   */
  async search(
    query: string,
    forceRefresh: boolean = false
  ): Promise<SetupMatter[]> {
    return this.getAll({ search: query } as SetupMatterFilterOptions, forceRefresh);
  }

  /**
   * Clear all cached data
   */
  clearCache(): void {
    this.cache.clear();
    console.debug('[SetupMatterService] Cache cleared');
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats(): { entries: number; keys: string[] } {
    return {
      entries: this.cache.size,
      keys: Array.from(this.cache.keys()),
    };
  }

  /**
   * Set cache TTL (time-to-live)
   *
   * @param ttlMs - TTL in milliseconds
   */
  setCacheTTL(ttlMs: number): void {
    this.cacheTTL = ttlMs;
  }

  /**
   * Format setup_matter for dropdown display
   *
   * @param item - SetupMatter object
   * @returns Formatted string for display
   */
  formatForDropdown(item: SetupMatter): string {
    // Use code-name pattern if available, otherwise just name or id
    const code = (item as any).code;
    const name = (item as any).name;

    if (code && name) {
      return `${code} - ${name}`;
    }
    if (name) {
      return name;
    }
    return item.id;
  }

  /**
   * Get setup_matters formatted for dropdown selection
   *
   * @param forceRefresh - Skip cache and fetch fresh data
   * @returns Array of { value, label, data } objects for dropdown
   */
  async getForDropdown(
    forceRefresh: boolean = false
  ): Promise<Array<{ value: string; label: string; data: SetupMatter }>> {
    const items = await this.getActive(forceRefresh);
    return items.map((item) => ({
      value: item.id,
      label: this.formatForDropdown(item),
      data: item,
    }));
  }

  // ---- CODAIC:CUSTOM METHODS ----
  // Add custom service methods below. This section is preserved across regeneration.
  // Use RESOURCE_PATH for URLs: e.g. `${RESOURCE_PATH}/my-action`
  // ---- CODAIC:END CUSTOM ----
}

// Export singleton instance for convenience
export const setup_matterService = new SetupMatterService();

// Export the class for testing and custom instantiation
export { SetupMatterService };

// Default export for convenience
export default setup_matterService;
